/**
 * Folders for organising projects, per discipline - /api/v2/folders. A folder is a way of looking
 * at projects, held apart from them: moving a project changes nothing about it, and deleting a
 * folder unfiles what was in it.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useAuthStore } from './auth';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

const BASE = '/api/v2/folders';

export interface ProjectFolder {
  id: string;
  discipline: string;
  name: string;
  createdAt: string;
}

export interface FolderView {
  folders: ProjectFolder[];
  /** project id -> folder id */
  placements: Record<string, string>;
  /** sample key -> the user's own copy's project id */
  samples: Record<string, string>;
}

const EMPTY: FolderView = { folders: [], placements: {}, samples: {} };

interface FolderState {
  byDiscipline: Record<string, FolderView>;
  error: string | null;
  load: (discipline: string) => Promise<void>;
  createFolder: (discipline: string, name: string) => Promise<void>;
  renameFolder: (discipline: string, folderId: string, name: string) => Promise<void>;
  deleteFolder: (discipline: string, folderId: string) => Promise<void>;
  move: (discipline: string, projectId: string, folderId: string | null) => Promise<void>;
}

async function request(url: string, init?: RequestInit): Promise<FolderView> {
  const response = await fetch(url, init);
  if (!response.ok) {
    let detail = `${response.status}`;
    try {
      detail = (await response.json()).detail ?? detail;
    } catch {
      // not JSON
    }
    throw new Error(String(detail));
  }
  return (await response.json()) as FolderView;
}

export const useFolderStore = create<FolderState>()(
  immer((set) => {
    const apply = async (discipline: string, call: () => Promise<FolderView>) => {
      try {
        const view = await call();
        set((s) => {
          s.byDiscipline[discipline] = view;
          s.error = null;
        });
      } catch (error) {
        set((s) => {
          s.error = error instanceof Error ? error.message : String(error);
        });
      }
    };
    return {
      byDiscipline: {},
      error: null,
      load: (discipline) =>
        apply(discipline, () => request(`${BASE}?discipline=${encodeURIComponent(discipline)}`, { headers: authHeaders() })),
      createFolder: (discipline, name) =>
        apply(discipline, () =>
          request(BASE, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ discipline, name }) }),
        ),
      renameFolder: (discipline, folderId, name) =>
        apply(discipline, () =>
          request(`${BASE}/${folderId}`, { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ name }) }),
        ),
      deleteFolder: (discipline, folderId) =>
        apply(discipline, () => request(`${BASE}/${folderId}`, { method: 'DELETE', headers: authHeaders() })),
      move: (discipline, projectId, folderId) =>
        apply(discipline, () =>
          request(`${BASE}/placements`, {
            method: 'PUT',
            headers: jsonHeaders(),
            body: JSON.stringify({ discipline, projectId, folderId }),
          }),
        ),
    };
  }),
);

export function useFolderView(discipline: string): FolderView {
  return useFolderStore((s) => s.byDiscipline[discipline]) ?? EMPTY;
}

/** Where each discipline duplicates a project, and where the copy's id is in the reply. */
const COPY_ENDPOINTS: Record<string, { url: (id: string) => string; id: (body: any) => string | undefined }> = {
  accounting: { url: (id) => `/api/v2/models/${id}/duplicate`, id: (b) => b?.id },
  'm-accounting': { url: (id) => `/api/m/projects/${id}/duplicate`, id: (b) => b?.project?.id },
  valuation: { url: (id) => `/api/v2/valuations/${id}/duplicate`, id: (b) => b?.id },
  diligence: { url: (id) => `/api/v2/diligence/${id}/duplicate`, id: (b) => b?.id },
};

/** Where each discipline keeps its samples: `${base}/${key}/reset` and `${base}/${key}/copy`. */
const SAMPLE_BASES: Record<string, string> = {
  accounting: '/api/v2/models/samples',
  'm-accounting': '/api/m/samples',
  valuation: '/api/v2/valuations/samples',
  diligence: '/api/v2/diligence/examples',
};

async function sampleCall(discipline: string, key: string, action: 'reset' | 'copy', body?: object): Promise<void> {
  const base = SAMPLE_BASES[discipline];
  if (!base) throw new Error(`${discipline} has no samples.`);
  const response = await fetch(`${base}/${encodeURIComponent(key)}/${action}`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.detail ?? `The ${action} failed (${response.status})`);
  }
  // A copy may have made a folder; a reset keeps the sample where it is. Either way, re-read.
  await useFolderStore.getState().load(discipline);
}

/** The user's sample put back as it shipped - the same project, whatever was changed in it. */
export function resetSample(discipline: string, key: string): Promise<void> {
  return sampleCall(discipline, key, 'reset');
}

/** A fresh copy of a sample, straight from the catalogue, as an ordinary project of the user's own in
 *  an existing folder (`folderId`) or a new one (`folderName`). Unlike the sample, it can be deleted. */
export function copySample(
  discipline: string,
  key: string,
  target: { folderId?: string; folderName?: string },
): Promise<void> {
  return sampleCall(discipline, key, 'copy', target);
}

/** Copy a project - a new one with the same content and its own history - and, where a folder is
 *  given, file the copy there. Returns the copy's id. The original stays where it was. */
export async function copyProject(discipline: string, projectId: string, folderId: string | null): Promise<string> {
  const endpoint = COPY_ENDPOINTS[discipline];
  if (!endpoint) throw new Error(`Projects in ${discipline} cannot be copied.`);
  const response = await fetch(endpoint.url(encodeURIComponent(projectId)), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.detail ?? `The copy failed (${response.status})`);
  }
  const copyId = endpoint.id(await response.json());
  if (!copyId) throw new Error('The copy was made but its id was not returned.');
  if (folderId) await useFolderStore.getState().move(discipline, copyId, folderId);
  return copyId;
}
